import { ArrowLeft, ArrowRight, Button, Delete, Heading } from '@core/ui';
import { t } from 'i18next';
import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { alignDurationToKeyframe, alignToKeyframe, CutTimelineEvent, Timeline } from '../../../parser';
import { TimestampInput } from '../../molecules';

export const TrimVideoEditorOrganism = ({ timeline, currentTime, onSeek }: { timeline: Timeline; currentTime: Duration; onSeek: (seek: Duration) => void }) => {
    const [currentCut, setCurrentCut] = useState<CutTimelineEvent>();
    const [previousCut, setPreviousCut] = useState<CutTimelineEvent>();
    const [nextCut, setNextCut] = useState<CutTimelineEvent>();
    const [currentCutIndex, setCurrentCutIndex] = useState<number>(-1);

    useEffect(() => {
        if (!timeline) {
            setPreviousCut(undefined);
            setCurrentCut(undefined);
            setNextCut(undefined);
            return;
        }

        let prevCut: CutTimelineEvent | undefined = undefined;
        let currCut: CutTimelineEvent | undefined = undefined;
        let currIndex: number = -1;
        let nextCut: CutTimelineEvent | undefined = undefined;
        for (let i = 0; i < timeline.cuts.length; i++) {
            if (timeline.cuts[i].timestamp <= currentTime) {
                if (i > 0) {
                    prevCut = timeline.cuts[i - 1];
                }

                currCut = timeline.cuts[i];
                currIndex = i;

                if (i < timeline.cuts.length - 1) {
                    nextCut = timeline.cuts[i + 1];
                }
            }
        }

        if (!currCut && timeline.cuts.length > 0) {
            nextCut = timeline.cuts[0];
        }

        setPreviousCut(prevCut);
        setCurrentCut(currCut);
        setCurrentCutIndex(currIndex);
        setNextCut(nextCut);
    }, [timeline, currentTime]);

    const onDelete = () => {
        if (currentCutIndex === -1) {
            return;
        }

        timeline.cuts.splice(currentCutIndex, 1);
        timeline.cuts = [...timeline.cuts];
        timeline.changed = true;
    };

    const updateTimestamp = (timestamp: Duration) => {
        if (!currentCut) {
            return;
        }

        currentCut.timestamp = alignToKeyframe(timeline, timestamp);
        currentCut.duration = alignDurationToKeyframe(timeline, currentCut.timestamp, currentCut.desiredDuration);
        currentCut.changed = true;
    };

    return (
        <div className="bg-gray-100 border border-gray-500 flex flex-col gap-4 pb-2">
            <div className="flex-1 flex flex-col bg-white rounded-b-webinarsub p-4">
                <div className="flex items-center justify-between gap-2 mb-4">
                    <Heading variant="h2">{t('Editor.TrimVideo.Heading')}</Heading>
                    {currentCut && (
                        <>
                            <Button variant="warning-outline" onClick={onDelete} iconbtn classNames="ms-auto">
                                <Delete classNames="size-6 flex-none stroke-current" />
                                <span className="sr-only">{t('Editor.TrimVideo.Button.Delete')}</span>
                            </Button>
                        </>
                    )}
                </div>
                <div className="flex-1 flex items-center justify-center gap-x-2">
                    <Button variant="primary" iconbtn onClick={() => onSeek(previousCut?.timestamp || Duration.fromMillis(0))} disabled={!previousCut}>
                        <span className="sr-only">{t('Editor.TrimVideo.Button.Previous')}</span>
                        <ArrowLeft classNames="size-6 fill-current" />
                    </Button>
                    <Button variant="primary" iconbtn onClick={() => onSeek(nextCut?.timestamp || Duration.fromMillis(0))} disabled={!nextCut}>
                        <span className="sr-only">{t('Editor.TrimVideo.Button.Next')}</span>
                        <ArrowRight classNames="size-6 fill-current" />
                    </Button>
                </div>
            </div>
            {currentCut && <TimestampInput timestamp={currentCut.timestamp} onChange={updateTimestamp} />}
        </div>
    );
};
